import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, BookOpen, X, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);

function Editor({ article, categories, onClose, onSaved }) {
  const [form, setForm] = useState(article || { title: "", category: "faq", content: "", approved: false });
  const [busy, setBusy] = useState(false);
  const inp = "w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm";
  const save = async () => {
    if (form.title.trim().length < 2 || form.content.trim().length < 5) { toast.error("Title & content required"); return; }
    setBusy(true);
    try {
      const body = { title: form.title, category: form.category, content: form.content, approved: form.approved };
      if (article?.id) await api.patch(`/admin/ops/kb/${article.id}`, body);
      else await api.post("/admin/ops/kb", body);
      toast.success("Article saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
        <div className="w-full max-w-lg bg-white rounded-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="kb-editor">
          <div className="flex justify-between items-center"><h3 className="font-bold text-slate-900">{article ? "Edit article" : "New knowledge base article"}</h3><button onClick={onClose}><X size={18} className="text-slate-400"/></button></div>
          <div className="mt-4 space-y-3">
            <label className="block text-xs text-slate-500">Title<input value={form.title} onChange={(e)=>setForm(f=>({...f,title:e.target.value}))} className={inp} data-testid="kb-title"/></label>
            <label className="block text-xs text-slate-500">Category
              <select value={form.category} onChange={(e)=>setForm(f=>({...f,category:e.target.value}))} className={inp} data-testid="kb-category">
                {categories.map(c=><option key={c} value={c}>{c.replace(/_/g," ")}</option>)}
              </select></label>
            <label className="block text-xs text-slate-500">Content (used by the AI for customer-facing replies when approved)
              <textarea rows={7} value={form.content} onChange={(e)=>setForm(f=>({...f,content:e.target.value}))} className={inp} data-testid="kb-content"/></label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.approved} onChange={(e)=>setForm(f=>({...f,approved:e.target.checked}))} className="accent-emerald-600" data-testid="kb-approved"/>
              Approved for AI use
            </label>
          </div>
          <button onClick={save} disabled={busy} className="mt-4 w-full py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm" data-testid="kb-save">{busy ? "Saving…" : "Save article"}</button>
        </div>
      </div>
    </ModalPortal>
  );
}

export default function AdminOpsKB() {
  const [data, setData] = useState(null);
  const [editor, setEditor] = useState(null); // null | "new" | article
  const load = useCallback(() => { api.get("/admin/ops/kb").then(r=>setData(r.data)).catch(()=>toast.error("Failed")); }, []);
  useEffect(load, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/ops/kb/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };
  if (!data) return <Loader2 className="animate-spin mx-auto my-10 text-emerald-600"/>;
  return (
    <div data-testid="aiops-kb">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">The AI Command box and Communication Agent only use <b>approved</b> articles for customer-facing wording.</p>
        <button onClick={()=>setEditor("new")} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="kb-new"><Plus size={13}/> New article</button>
      </div>
      {data.rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-400"><BookOpen size={24} className="mx-auto mb-2 text-slate-300"/>No knowledge base articles yet. Add your refund policy, FAQs, plan details and email templates here.</Card> : (
        <div className="grid md:grid-cols-2 gap-3">
          {data.rows.map(a => (
            <Card key={a.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">{a.category.replace(/_/g," ")}</span>
                    {a.approved ? <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full uppercase">approved</span>
                                 : <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full uppercase">draft</span>}
                  </div>
                  <div className="font-bold text-slate-900 mt-1">{a.title}</div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.content}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={()=>setEditor(a)} className="p-1.5 text-slate-400 hover:text-emerald-600" data-testid={`kb-edit-${a.id.slice(0,6)}`}><Pencil size={14}/></button>
                  <button onClick={()=>del(a.id)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`kb-del-${a.id.slice(0,6)}`}><Trash2 size={14}/></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {editor && <Editor article={editor === "new" ? null : editor} categories={data.categories}
        onClose={()=>setEditor(null)} onSaved={()=>{ setEditor(null); load(); }}/>}
    </div>
  );
}
